import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  FileText, 
  Brain, 
  Target, 
  CheckCircle, 
  GraduationCap,
  School,
  BookOpen,
  Star
} from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const HowItWorksPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9F7FE] to-[#EADFFF] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Your Journey to the <br />
              <span className="text-[#7B4AE2]">Perfect College Match</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              A simple, data-driven approach to finding and getting admitted to your ideal college
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Follow these simple steps to find and get admitted to your perfect college
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="relative flex items-center mb-16">
              <div className="hidden md:block w-24 border-t-2 border-[#7B4AE2] absolute left-0"></div>
              <div className="flex-shrink-0 w-16 h-16 bg-[#F0E9FF] rounded-full flex items-center justify-center z-10">
                <FileText className="h-8 w-8 text-[#7B4AE2]" />
              </div>
              <div className="ml-8 flex-grow">
                <h3 className="text-xl font-semibold mb-2">1. Upload Your Marksheet</h3>
                <p className="text-gray-600 mb-4">
                  Simply upload your 10th standard marksheet. Our AI system will automatically process and analyze your academic performance.
                </p>
                <div className="bg-[#F9F7FE] rounded-lg p-4">
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Secure document processing
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Instant data extraction
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Accurate score analysis
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative flex items-center mb-16">
              <div className="hidden md:block w-24 border-t-2 border-[#18CDCA] absolute left-0"></div>
              <div className="flex-shrink-0 w-16 h-16 bg-[#E0F9F9] rounded-full flex items-center justify-center z-10">
                <Brain className="h-8 w-8 text-[#18CDCA]" />
              </div>
              <div className="ml-8 flex-grow">
                <h3 className="text-xl font-semibold mb-2">2. Get AI-Powered Predictions</h3>
                <p className="text-gray-600 mb-4">
                  Our advanced AI analyzes your profile against historical data to predict your admission chances at various colleges.
                </p>
                <div className="bg-[#F9F7FE] rounded-lg p-4">
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      95% prediction accuracy
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Personalized college matches
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Round-wise cutoff predictions
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative flex items-center mb-16">
              <div className="hidden md:block w-24 border-t-2 border-[#FFD166] absolute left-0"></div>
              <div className="flex-shrink-0 w-16 h-16 bg-[#FFF5D9] rounded-full flex items-center justify-center z-10">
                <Target className="h-8 w-8 text-[#FFD166]" />
              </div>
              <div className="ml-8 flex-grow">
                <h3 className="text-xl font-semibold mb-2">3. Compare and Select Colleges</h3>
                <p className="text-gray-600 mb-4">
                  Compare up to 10 colleges side by side, analyzing everything from cutoffs to facilities.
                </p>
                <div className="bg-[#F9F7FE] rounded-lg p-4">
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Detailed college insights
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Side-by-side comparison
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Smart recommendations
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative flex items-center">
              <div className="hidden md:block w-24 border-t-2 border-[#EF476F] absolute left-0"></div>
              <div className="flex-shrink-0 w-16 h-16 bg-[#FFE9ED] rounded-full flex items-center justify-center z-10">
                <GraduationCap className="h-8 w-8 text-[#EF476F]" />
              </div>
              <div className="ml-8 flex-grow">
                <h3 className="text-xl font-semibold mb-2">4. Apply with Confidence</h3>
                <p className="text-gray-600 mb-4">
                  Get round-wise guidance on when and where to apply based on your admission chances.
                </p>
                <div className="bg-[#F9F7FE] rounded-lg p-4">
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Strategic application planning
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Round-wise guidance
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Application tracking
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="bg-[#F9F7FE] py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Everything You Need</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform provides all the tools and insights you need for successful college admission
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6">
              <School className="h-8 w-8 text-[#7B4AE2] mb-4" />
              <h3 className="text-xl font-semibold mb-2">College Database</h3>
              <p className="text-gray-600">
                Comprehensive information about colleges across major cities
              </p>
            </div>

            <div className="bg-white rounded-lg p-6">
              <BookOpen className="h-8 w-8 text-[#18CDCA] mb-4" />
              <h3 className="text-xl font-semibold mb-2">Cutoff Analysis</h3>
              <p className="text-gray-600">
                Historical cutoff data and future predictions
              </p>
            </div>

            <div className="bg-white rounded-lg p-6">
              <Star className="h-8 w-8 text-[#FFD166] mb-4" />
              <h3 className="text-xl font-semibold mb-2">Smart Recommendations</h3>
              <p className="text-gray-600">
                AI-powered college suggestions based on your profile
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-[#7B4AE2] to-[#5A35A7] rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Start Your College Journey Today
            </h2>
            <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto">
              Join thousands of students who have found their perfect college match using our platform
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-8 py-4 bg-white text-[#7B4AE2] rounded-lg hover:bg-opacity-90 transition-colors font-medium"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;